import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Eye, EyeOff, Plug, RefreshCw } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

async function fetchIntegrations() {
  const res = await apiClient.get('/integrations');
  return (res.data as any).data as {
    apollo: {
      configured: boolean;
      key_preview: string | null;
      label: string;
    };
  };
}

async function saveApolloKey(apiKey: string) {
  await apiClient.post('/integrations', { api_key: apiKey });
}

async function testApolloConnection() {
  const res = await apiClient.post('/integrations/test', {});
  return (res.data as any).data as { connected: boolean; message: string; sample_count: number };
}

// ---------------------------------------------------------------------------
// IntegrationsPage
// ---------------------------------------------------------------------------

type TestStatus = 'idle' | 'testing' | 'ok' | 'fail';

export default function IntegrationsPage() {
  const qc = useQueryClient();
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testStatus, setTestStatus] = useState<TestStatus>('idle');
  const [testMessage, setTestMessage] = useState('');
  const abortRef = useRef(false);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-integrations'],
    queryFn: fetchIntegrations,
  });

  const apollo = data?.apollo;

  const handleSave = async () => {
    if (!apiKeyInput.trim()) return;
    setSaving(true);
    try {
      await saveApolloKey(apiKeyInput.trim());
      toast.success('Apollo API key saved successfully.');
      setApiKeyInput('');
      setShowKey(false);
      setTestStatus('idle');
      qc.invalidateQueries({ queryKey: ['admin-integrations'] });
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to save API key.');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    abortRef.current = false;
    setTestStatus('testing');
    setTestMessage('');
    try {
      const result = await testApolloConnection();
      if (!abortRef.current) {
        setTestStatus('ok');
        setTestMessage(result.message ?? 'Connection verified.');
        toast.success('Apollo connection verified.');
      }
    } catch (err: unknown) {
      if (!abortRef.current) {
        setTestStatus('fail');
        setTestMessage(err instanceof Error ? err.message : 'Connection failed.');
        toast.error('Apollo connection failed. Check your API key.');
      }
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Integrations</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Manage third-party provider API keys used for lead discovery and enrichment.
        </p>
      </div>

      {/* Apollo.io card */}
      <div className="rounded-xl border bg-card p-6 space-y-5 max-w-xl">
        {/* Header row */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-muted">
              <Plug className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">Apollo.io</h2>
              <p className="text-sm text-muted-foreground">People search &amp; email enrichment</p>
            </div>
          </div>
          {!isLoading && (
            <Badge variant={apollo?.configured ? 'default' : 'secondary'}>
              {apollo?.configured ? 'Connected' : 'Not configured'}
            </Badge>
          )}
        </div>

        {/* Current key preview */}
        {apollo?.configured && apollo.key_preview && (
          <p className="text-sm text-muted-foreground">
            Current key:&nbsp;
            <span className="font-mono font-medium text-foreground">{apollo.key_preview}</span>
          </p>
        )}

        {/* Key input */}
        <div className="space-y-2">
          <Label htmlFor="apollo-key">
            {apollo?.configured ? 'Replace API Key' : 'API Key'}
          </Label>
          <p className="text-xs text-muted-foreground">
            Obtain your key from{' '}
            <span className="font-medium text-foreground">
              apollo.io &rsaquo; Settings &rsaquo; API Keys
            </span>
          </p>
          <div className="flex gap-2">
            <Input
              id="apollo-key"
              type={showKey ? 'text' : 'password'}
              placeholder="Enter Apollo API key…"
              value={apiKeyInput}
              onChange={(e) => setApiKeyInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSave()}
              className="font-mono text-sm"
            />
            <Button
              variant="outline"
              size="icon"
              onClick={() => setShowKey((v) => !v)}
              title={showKey ? 'Hide key' : 'Show key'}
            >
              {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={handleSave}
            disabled={!apiKeyInput.trim() || saving}
          >
            {saving ? 'Saving…' : 'Save Key'}
          </Button>
          <Button
            variant="outline"
            onClick={handleTest}
            disabled={testStatus === 'testing' || !apollo?.configured}
            title={!apollo?.configured ? 'Save a key first to test the connection' : undefined}
          >
            {testStatus === 'testing' ? (
              <>
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                Testing…
              </>
            ) : (
              'Test Connection'
            )}
          </Button>
        </div>

        {/* Test result */}
        {testStatus === 'ok' && (
          <p className="text-sm font-medium text-green-600">{testMessage || 'Connection verified.'}</p>
        )}
        {testStatus === 'fail' && (
          <p className="text-sm font-medium text-destructive">{testMessage || 'Connection failed. Check your key.'}</p>
        )}
      </div>

      {/* Placeholder for future providers */}
      <p className="text-xs text-muted-foreground">
        More integrations (ZoomInfo, LinkedIn Sales Navigator, Hunter.io) coming soon.
      </p>
    </div>
  );
}
